import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSpreadsheet, Trash2, CheckCircle2, XCircle, Loader2,
  Clock, AlertTriangle, Upload, RefreshCw, ArrowLeft,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_MAP = {
  pending:     { label: "بانتظار التنفيذ", color: "text-[var(--text-muted)]",    bg: "bg-[var(--bg-card-hover)]"    },
  running:     { label: "جارٍ...",          color: "text-blue-500",   bg: "bg-blue-50"     },
  success:     { label: "اكتمل",            color: "text-emerald-600", bg: "bg-emerald-50" },
  partial:     { label: "جزئي",             color: "text-amber-600",  bg: "bg-amber-50"    },
  failed:      { label: "فشل",              color: "text-red-500",    bg: "bg-red-50"      },
  rolled_back: { label: "تراجع",            color: "text-[var(--text-muted)]",   bg: "bg-[var(--bg-card-hover)]"    },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.bg} ${s.color}`}>
      {status === "success"     && <CheckCircle2 className="w-3 h-3" />}
      {status === "failed"      && <XCircle className="w-3 h-3" />}
      {status === "running"     && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === "partial"     && <AlertTriangle className="w-3 h-3" />}
      {s.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ar-SA", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function ImportHistory() {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/import/xlsx/sessions", { params: { limit: 100 } });
      setSessions(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteSession = async (id) => {
    try {
      await api.delete(`/import/xlsx/session/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      toast.success("تم حذف الجلسة");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /* aggregate stats */
  const totalCreated = sessions.reduce((s, x) => s + (x.progress?.created || 0), 0);
  const totalFailed  = sessions.reduce((s, x) => s + (x.progress?.failed  || 0), 0);
  const totalProducts = sessions.reduce((s, x) => s + (x.stats?.products_count || 0), 0);
  const rate = totalCreated + totalFailed > 0
    ? Math.round((totalCreated / (totalCreated + totalFailed)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">

        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4 fade-up">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">الاستيراد</p>
            <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-gray-900 mt-1">سجل الاستيرادات</h1>
            <p className="text-[var(--text-muted)] mt-2 text-sm">تاريخ كل الملفات المرفوعة ونتائج إنشائها في سلة.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 border border-[var(--border-color)] rounded-xl text-sm hover:border-gray-500 transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              تحديث
            </button>
            <button onClick={() => navigate("/import")}
              data-testid="go-to-import-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0044FF] hover:bg-[#0033CC] text-white rounded-xl text-sm font-medium transition-colors">
              <Upload className="w-4 h-4 rtl-flip" />
              رفع ملف جديد
            </button>
          </div>
        </header>

        {/* Summary Cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="history-summary-cards">
          {[
            { label: "إجمالي الجلسات",       value: sessions.length.toLocaleString("en-US"), color: "text-[#0044FF]",   bg: "bg-blue-50   border-blue-200"   },
            { label: "منتجات في الملفات",     value: totalProducts.toLocaleString("en-US"),   color: "text-[var(--text-secondary)]",   bg: "bg-[var(--bg-card-hover)]   border-[var(--border-color)]"   },
            { label: "أُنشئت في سلة",         value: totalCreated.toLocaleString("en-US"),    color: "text-emerald-600",bg: "bg-emerald-50 border-emerald-200"},
            { label: "معدل النجاح",            value: `${rate.toLocaleString("en-US")}%`,     color: "text-amber-600",  bg: "bg-amber-50  border-amber-200"  },
          ].map((c) => (
            <div key={c.label} className={`border rounded-xl p-5 ${c.bg}`}>
              <div className={`text-2xl font-bold font-mono ${c.color}`}>{c.value}</div>
              <div className="text-xs text-[var(--text-muted)] mt-1">{c.label}</div>
            </div>
          ))}
        </section>

        {/* Sessions table */}
        <section className="bg-white border border-[var(--border-color)] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]/50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-secondary)] flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--text-muted)]" />
              الجلسات ({sessions.length.toLocaleString("en-US")})
            </h2>
          </div>

          {loading && (
            <div className="py-16 flex flex-col items-center gap-3 text-[var(--text-muted)]">
              <Loader2 className="w-7 h-7 animate-spin text-[#0044FF]" />
              <span className="text-sm">جارٍ التحميل...</span>
            </div>
          )}

          {!loading && sessions.length === 0 && (
            <div className="py-16 flex flex-col items-center gap-4 text-[var(--text-muted)]">
              <div className="w-14 h-14 rounded-2xl bg-[var(--bg-card-hover)] flex items-center justify-center">
                <FileSpreadsheet className="w-7 h-7 text-[var(--text-muted)]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[var(--text-muted)]">لا توجد جلسات بعد</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">ارفع أول ملف Excel من صفحة الاستيراد</p>
              </div>
              <button onClick={() => navigate("/import")}
                className="flex items-center gap-2 px-4 py-2 bg-[#0044FF] text-white rounded-xl text-sm font-medium hover:bg-[#0033CC] transition-colors">
                <Upload className="w-4 h-4 rtl-flip" />
                استيراد الآن
              </button>
            </div>
          )}

          {!loading && sessions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b border-[var(--border-muted)] bg-[var(--bg-card-hover)]/80">
                    <th className="text-start px-6 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الملف</th>
                    <th className="text-start px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">تاريخ الرفع</th>
                    <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">المنتجات</th>
                    <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">المقاسات</th>
                    <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">أُنشئ</th>
                    <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">فشل</th>
                    <th className="text-center px-4 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">الحالة</th>
                    <th className="text-center px-6 py-3 text-[10px] tracking-widest uppercase text-[var(--text-muted)] font-semibold">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b border-[var(--border-muted)] hover:bg-[var(--bg-card-hover)]/60 transition-colors"
                      data-testid={`history-row-${s.id}`}>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                            <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-800 truncate max-w-[200px]">{s.filename || "—"}</div>
                            <div className="text-[10px] text-[var(--text-muted)] font-mono">{s.id?.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[var(--text-muted)] text-xs whitespace-nowrap">{formatDate(s.uploaded_at)}</td>
                      <td className="px-4 py-3.5 text-center font-mono font-medium text-[var(--text-secondary)]">
                        {(s.stats?.products_count ?? 0).toLocaleString("en-US")}
                      </td>
                      <td className="px-4 py-3.5 text-center font-mono text-[var(--text-muted)]">
                        {(s.stats?.variants_count ?? 0).toLocaleString("en-US")}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="font-mono font-semibold text-emerald-600">
                          {(s.progress?.created ?? 0).toLocaleString("en-US")}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className={`font-mono font-semibold ${s.progress?.failed ? "text-red-500" : "text-[var(--text-muted)]"}`}>
                          {(s.progress?.failed ?? 0).toLocaleString("en-US")}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <StatusBadge status={s.status || "pending"} />
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate("/import")}
                            className="p-1.5 border border-[var(--border-color)] rounded-lg hover:border-[#0044FF] hover:text-[#0044FF] transition-colors"
                            title="فتح الجلسة"
                          >
                            <ArrowLeft className="w-3.5 h-3.5 rtl-flip" />
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-1.5 border border-[var(--border-color)] rounded-lg hover:border-red-300 hover:text-red-500 transition-colors" title="حذف الجلسة">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent dir="rtl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>حذف الجلسة</AlertDialogTitle>
                                <AlertDialogDescription>
                                  سيتم حذف جلسة «{s.filename}» من السجل. لن يؤثر على المنتجات التي أُنشئت في سلة.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteSession(s.id)} className="bg-red-500 hover:bg-red-600">
                                  نعم، احذف
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
